// Newsletter Subscription Service
// Handles API calls for newsletter subscriptions

import { config } from '@/config';

// Use base URL without /api/v1 suffix for newsletter endpoint
const API_BASE_URL = config.api.baseUrl.replace('/api/v1', '');

export interface NewsletterSubscribeData {
  email: string;
  name?: string;
}

export interface NewsletterResponse {
  success: boolean;
  message: string;
  data?: {
    email: string;
    subscribedAt: string;
  };
}

export interface NewsletterError {
  status: string;
  message: string;
  code?: string;
  details?: Array<{
    type: string;
    msg: string;
    path: string;
    location: string;
  }>;
}

/**
 * Subscribe to the newsletter
 */
async function subscribe(email: string, name?: string): Promise<NewsletterResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/newsletter/subscribe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, name }),
    });

    const result = await response.json();

    if (!response.ok) {
      const error = result as NewsletterError;

      // Handle validation errors
      if (error.details && error.details.length > 0) {
        const fieldErrors = error.details.map((d) => d.msg).join(", ");
        return {
          success: false,
          message: fieldErrors,
        };
      }

      // Handle duplicate email (already subscribed)
      if (response.status === 409) {
        return {
          success: true,
          message: "You're already subscribed! We'll keep you updated.",
        };
      }

      return {
        success: false,
        message: error.message || "Failed to subscribe. Please try again.",
      };
    }

    return result as NewsletterResponse;
  } catch (error) {
    console.error("Newsletter subscription error:", error);
    return {
      success: false,
      message: "Unable to connect to server. Please try again later.",
    };
  }
}

/**
 * Unsubscribe from the newsletter
 */
async function unsubscribe(email: string): Promise<NewsletterResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/newsletter/unsubscribe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    const result = await response.json();

    if (!response.ok) {
      const error = result as NewsletterError;
      return {
        success: false,
        message: error.message || "Failed to unsubscribe. Please try again.",
      };
    }

    return result as NewsletterResponse;
  } catch (error) {
    console.error("Newsletter unsubscribe error:", error);
    return {
      success: false,
      message: "Unable to connect to server. Please try again later.",
    };
  }
}

export const newsletterService = {
  subscribe,
  unsubscribe,
};
